import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import * as api from '../api';
import GalaxyMap from './three/GalaxyMap';

function MissionPanel() {
  const { ships, drones, updateInventory } = useGame();
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState([]);
  const [selectedShip, setSelectedShip] = useState('');
  const [selectedDrones, setSelectedDrones] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('info');
  const [, setTick] = useState(0);
  const timerRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [tRes, aRes] = await Promise.all([
        api.getMissionTemplates(),
        api.getActiveMissions(),
      ]);
      setTemplates(tRes.data.templates || []);
      setActive(aRes.data.missions || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const showMsg = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(null), 5000);
  };

  const handleStart = async (templateId) => {
    if (!selectedShip) { showMsg('Select a ship first', 'warn'); return; }
    try {
      await api.startMission(Number(templateId), Number(selectedShip), selectedDrones.map(Number));
      await loadData();
      showMsg('Mission deployed — fleet en route', 'success');
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to start mission', 'error');
    }
  };

  const handleClaim = async (missionId) => {
    try {
      const { data } = await api.claimMission(missionId);
      if (data.inventory) updateInventory(data.inventory);
      const eventLabel = data.mission?.event_log?.label || '';
      const rewards = data.rewards || {};
      const rewardStr = Object.entries(rewards).map(([k, v]) => `+${v.toLocaleString()} ${k.replace(/_/g, ' ')}`).join('  ·  ');
      showMsg(`${rewardStr}${eventLabel ? `  —  ${eventLabel}` : ''}`, 'success');
      await loadData();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to claim', 'error');
    }
  };

  const fmtTime = (seconds) => {
    if (seconds <= 0) return 'READY';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
  };

  const toggleDrone = (id) => {
    setSelectedDrones(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const busyShipIds = new Set(active.filter(m => m.status === 'active').map(m => m.ship_id));
  const busyDroneIds = new Set(active.filter(m => m.status === 'active').flatMap(m => m.drone_ids || []));

  // Mapping from galaxy map sector keys to mission template sector names
  const SECTOR_MISSION_MAP = {
    asteroid_belt: ['Inner Belt', 'Mid Belt'],
    deep_core: ['Mid Belt'],
    nebula: ['Nebula Zone'],
    frontier: ['Outer Reach'],
    trade_route: ['Inner Belt', 'Outer Reach'],
  };

  // Filter templates by selected sector
  const filteredTemplates = selectedSector
    ? templates.filter(t => {
        const mapped = SECTOR_MISSION_MAP[selectedSector.key] || [];
        return mapped.includes(t.sector);
      })
    : templates;

  return (
    <div className="panel mission-panel">
      {msg && <div className={`hud-toast hud-toast-${msgType}`}>{msg}</div>}

      {/* 3D Galaxy Map */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            <span className="title-accent">◎</span> GALACTIC SECTOR MAP
          </h3>
          {selectedSector && (
            <button className="btn-ghost" onClick={() => setSelectedSector(null)}>Clear filter</button>
          )}
        </div>
        <GalaxyMap onSectorSelect={setSelectedSector} selectedSector={selectedSector} />
      </section>

      {/* Active Missions */}
      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">▶</span> ACTIVE OPERATIONS
          <span className="badge">{active.length}</span>
        </h3>
        {active.length === 0 && <p className="hud-empty">No active operations. Deploy a fleet below.</p>}
        <div className="hud-grid">
          {active.map(m => {
            const remaining = (new Date(m.ends_at) - Date.now()) / 1000;
            const isReady = remaining <= 0;
            const total = (new Date(m.ends_at) - new Date(m.started_at)) / 1000;
            const progress = isReady ? 100 : Math.min(100, Math.max(0, ((total - remaining) / total) * 100));

            return (
              <div key={m.id} className={`hud-card ${isReady ? 'hud-card-ready' : ''}`}>
                <div className="hud-card-header">
                  <span className="card-name">{m.template?.name || 'Mission'}</span>
                  <span className={`card-status ${isReady ? 'status-ready' : 'status-active'}`}>
                    {isReady ? 'READY' : 'IN TRANSIT'}
                  </span>
                </div>
                <div className="card-meta">
                  <span>Sector: {m.template?.sector}</span>
                  <span>Risk: {m.template?.risk_level || '—'}</span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{width: `${progress}%`}} />
                </div>
                <div className="card-timer">{fmtTime(remaining)}</div>
                {isReady && (
                  <button className="btn btn-glow" onClick={() => handleClaim(m.id)}>
                    CLAIM REWARDS
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Fleet Deployment */}
      <section className="section">
        <h3 className="section-title"><span className="title-accent">▲</span> DEPLOY FLEET</h3>
        <div className="deploy-hud">
          <div className="deploy-select">
            <label className="hud-label">Ship</label>
            <select className="hud-select" value={selectedShip} onChange={e => setSelectedShip(e.target.value)}>
              <option value="">— Select Ship —</option>
              {ships.filter(s => !busyShipIds.has(s.id)).map(s => (
                <option key={s.id} value={s.id}>{s.name} · Lv{s.level}</option>
              ))}
            </select>
          </div>
          <div className="deploy-drones">
            <label className="hud-label">Drones</label>
            <div className="drone-grid">
              {drones.filter(d => !busyDroneIds.has(d.id)).map(d => (
                <label key={d.id} className={`drone-chip ${selectedDrones.includes(d.id) ? 'drone-active' : ''}`}>
                  <input type="checkbox" checked={selectedDrones.includes(d.id)} onChange={() => toggleDrone(d.id)} />
                  <span>{d.name} · Lv{d.level}</span>
                </label>
              ))}
              {drones.filter(d => !busyDroneIds.has(d.id)).length === 0 && (
                <span className="hud-empty">All drones deployed</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Available Missions */}
      <section className="section">
        <h3 className="section-title">
          <span className="title-accent">◎</span> AVAILABLE MISSIONS
          {selectedSector && <span className="filter-tag">{selectedSector.name}</span>}
        </h3>
        <div className="hud-grid">
          {(filteredTemplates.length > 0 ? filteredTemplates : templates).map(t => (
            <div key={t.id} className="hud-card hud-card-template">
              <div className="hud-card-header">
                <span className="card-name">{t.name}</span>
                <span className={`risk-badge risk-${t.risk_level}`}>{t.risk_level}</span>
              </div>
              <p className="card-desc">{t.description}</p>
              <div className="card-stats">
                <span>⏱ {fmtTime(t.base_duration_s)}</span>
                <span>⚡ {t.energy_cost}</span>
                <span>📦 {t.resource_type?.replace(/_/g, ' ')}</span>
              </div>
              <button
                className="btn btn-deploy"
                onClick={() => handleStart(t.id)}
                disabled={!selectedShip}
              >
                {selectedShip ? 'DEPLOY' : 'SELECT SHIP'}
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default MissionPanel;
